import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { palettes, tokens } from '../theme/tokens';

const TRACK_PADDING = tokens.gap.inline;
const TRACK_HEIGHT = tokens.gap.section;
const THUMB_HEIGHT = TRACK_HEIGHT - TRACK_PADDING * 2;

const Container = styled(Flex)`
	position: relative;
	align-items: center;
	box-sizing: border-box;
	width: fit-content;
	height: ${TRACK_HEIGHT}px;
	padding: ${TRACK_PADDING}px;
	border-radius: ${tokens.radius.full}px;
	background: ${palettes.softBeige['500']};
	box-shadow: 0 0 0 1px ${tokens.border.subtle};

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: inherit;
		box-shadow: ${tokens.shadow.inset};
		pointer-events: none;
		z-index: 2;
	}
`;

const Segment = styled.div<{ $selected?: boolean }>`
	flex: 0 0 auto;
	min-width: max-content;
	height: ${THUMB_HEIGHT}px;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	z-index: 1;
	padding: 0 ${tokens.padding.compact}px;
	box-sizing: border-box;
	text-align: center;
	cursor: pointer;
	white-space: nowrap;
	font-size: ${tokens.typography.sizes.small}px;
	line-height: ${tokens.typography.lineHeights.small};
	letter-spacing: 0.13px;
	font-family: ${tokens.typography.fontFamily};
	font-weight: ${(props) =>
		props.$selected ? tokens.typography.weights.semibold : tokens.typography.weights.medium};
	border-radius: ${tokens.radius.full}px;
	color: ${(props) => (props.$selected ? tokens.text.primary : tokens.text.secondary)};

	&:hover {
		color: ${tokens.text.primary};
	}
`;

const Selected = styled(Box)`
	position: absolute;
	top: ${TRACK_PADDING}px;
	height: ${THUMB_HEIGHT}px;
	z-index: 0;
	box-sizing: border-box;
	background: ${tokens.surface.white};
	border: 1px solid ${tokens.border.subtle};
	box-shadow: ${tokens.shadow.button};
	border-radius: ${tokens.radius.full}px;
	pointer-events: none;
	transition:
		left 300ms cubic-bezier(0, 1, 0, 1),
		width 300ms cubic-bezier(0, 1, 0, 1);
`;

export function SegmentedControl(props: {
	value: string;
	options: { title: string; value: string }[];
	onChange: (value: string) => void;
}) {
	const segmentRefs = useMemo(
		() => Array.from({ length: props.options.length }, () => createRef<HTMLDivElement>()),
		[props.options.length],
	);
	const parentRef = useRef<HTMLDivElement | null>(null);
	const [selectedBounds, setSelectedBounds] = useState({ left: TRACK_PADDING, width: 100 });

	useEffect(() => {
		const selectedIndex = props.options.findIndex(({ value }) => value === props.value);
		const selected = segmentRefs[selectedIndex]?.current;
		const parent = parentRef.current;

		if (!selected || !parent) {
			return;
		}

		const bounds = selected.getBoundingClientRect();
		const parentBounds = parent.getBoundingClientRect();

		setSelectedBounds({
			left: bounds.left - parentBounds.left,
			width: bounds.width,
		});
	}, [segmentRefs, props.options, props.value]);

	return (
		<Container ref={parentRef} role="radiogroup">
			<Selected style={selectedBounds} />
			{props.options.map((option, index) => (
				<Segment
					key={option.value}
					role="radio"
					aria-checked={option.value === props.value}
					onClick={(element) => {
						const parent = parentRef.current;
						if (!parent) {
							return;
						}

						const bounds = element.currentTarget.getBoundingClientRect();
						const parentBounds = parent.getBoundingClientRect();
						setSelectedBounds({ left: bounds.left - parentBounds.left, width: bounds.width });

						props.onChange(option.value);
					}}
					$selected={option.value === props.value}
					ref={segmentRefs[index]}
				>
					{option.title}
				</Segment>
			))}
		</Container>
	);
}
